import React, { useEffect, useState } from 'react'
import StatusBadge from '../../../../../common/StatusBadge';
import ToastMessage from '../../../../../common/ToastMessage';
import FilterPurchaseComponent from './FilterPurchaseComponent';
import CustomDataGrid from '../../../../../common/CustomDataGrid';
import FilterBtn from '../../../Assets/Components/Header/FilterBtn';
import usePurchaseOrders from '../../../../../store/ReactQueries/procurements/purchaseOrders/usePurchaseOrders';
import useDeletePurchateOrder from '../../../../../store/ReactQueries/procurements/purchaseOrders/useDeletePurchateOrder';
import { GridColDef } from '@mui/x-data-grid';
import { StorageItems } from '../../../../../utils/storageItems';
import { FieldErrors } from '../../../../../domain/helpers/Errors';
import { DataSelectType } from '../../../../../models/DataSelectType';
import { useLanguageStore } from '../../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../../store/useDarkModeStore';
import { darkModeBackgroud, PAGESIZE } from '../../../../../utils/constants';
import { formatDate, formatNumber } from '../../../../../common/GlobalFunctions';
import { getPurchaseOrderStatusInfo, PurchaseOrderStatus } from '../../../../../enums/purchaseOrderStatus';
import { PurchaseOrderInterface } from '../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrder';
import { PurchaseOrderLineInterface } from '../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrderLine';

export const PurchaseOrders = () => {
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<PurchaseOrderInterface>({
        id: '',
        description: '',
        code: '',
        date: null,
        purchaseOrderLines: [],
        purchaseOrderTypeId: '',
        requestForQuotation: false,
        vendorId: '',
        status: PurchaseOrderStatus.Pending,
        uploadInvoices: [],
        hasInvoice: false,
    });
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filterData, setFilterData] = useState<DataSelectType[]>();
    let { data: purchaseOrders, error: purchaseOrderError, isLoading } = usePurchaseOrders(page, pageSize, filterData);

    const storedPage = localStorage.getItem(StorageItems.PurchasePage);
    const storedPageSize = localStorage.getItem(StorageItems.PurchasePageSize);

    localStorage.setItem(StorageItems.PurchasePage, page.toString());
    localStorage.setItem(StorageItems.PurchasePageSize, pageSize.toString());

    const deletePurchaseOrder = useDeletePurchateOrder(page, pageSize);

    useEffect(() => {
        if (purchaseOrderError) ToastMessage.Failed(`Error: ${purchaseOrderError.message}`);
    }, [purchaseOrderError]);

    useEffect(() => {
        setTimeout(() => {
            const previousLocation = localStorage.getItem('PreviousLocation');
            const isNotPurchasePath = !['/purchaseorders/', '/purchaseorder/'].some(path => previousLocation?.includes(path));
            if (previousLocation === '') {
                setPage(storedPage ? parseInt(storedPage) : 1);
                setPageSize(storedPageSize ? parseInt(storedPageSize) : 20);
            }
            else if (isNotPurchasePath) {
                setPage(1);
                setPageSize(PAGESIZE);
            } else {
                setPage(storedPage ? parseInt(storedPage) : 1);
                setPageSize(storedPageSize ? parseInt(storedPageSize) : 20);
            }
        }, 100);

    }, []);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'code', headerName: 'Code', type: "string", editable: false, width: 120 },
        { field: 'date', headerName: 'Date', type: "string", valueFormatter: formatDate, editable: false, width: 150 },
        {
            field: 'author',
            headerName: 'Author',
            type: "string",
            editable: false,
            width: 150,
            renderCell: ({ row }) => (
                <span>
                    {row.author ? `${row.author.firstname ?? ''} ${row.author.lastname ?? ''}` : ""}
                </span>
            ),
        },
        { field: 'description', headerName: 'Description', type: "string", editable: false, width: 150 },
        {
            field: 'purchaseOrderType',
            headerName: 'Purchase order type',
            type: "string",
            editable: false,
            width: 150,
            renderCell: ({ row }) => <div className="d-flex align-items-center justify-content-between">
                <span className="text-truncate" style={{ maxWidth: 100 }}>{row.purchaseOrderType ? row.purchaseOrderType.title : ''}</span>
            </div>
        },
        {
            field: 'vendor',
            headerName: 'Vendor',
            type: "string",
            editable: true,
            width: 150,
            renderCell: ({ row }) => (
                <span>
                    {row.vendor ? `${row.vendor.name ?? ''}` : ""}
                </span>
            ),
        },
        { field: 'requestForQuotation', headerName: 'Request for quotation', type: "boolean", editable: false, width: 100 },
        { field: 'hasInvoice', headerName: 'Has Invoice', type: "boolean", editable: false, width: 100 },
        {
            field: 'purchaseOrderLine',
            headerName: 'Total',
            type: "string",
            editable: false,
            width: 100,
            renderCell: ({ row }) => {
                const totalAmount = row.purchaseOrderLines
                    ? row.purchaseOrderLines.reduce(
                        (sum: number, line: PurchaseOrderLineInterface) => sum + (line.amount || 0),
                        0
                    ).toFixed(2)
                    : null;

                const total = row.discount ? totalAmount - row.discount : totalAmount;

                return (
                    <span>
                        {total ? `${formatNumber(Number(total))} €` : ""}
                    </span>
                );
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            type: "string",
            editable: false,
            width: 200,
            cellClassName: 'd-flex align-items-center',
            renderCell: ({ row }) => {
                const statusInfo = getPurchaseOrderStatusInfo(row.status as PurchaseOrderStatus);
                return <StatusBadge name={statusInfo.name} color={statusInfo.color} width='fit-content' fontSize={12} />
            },
        },
        { field: 'purchaseOrderTypeId', headerName: 'PurchaseOrderTypeId', type: "string", editable: false, width: 200 },
    ];

    const addItems = () => {
        setForm({ ...form, id: '', description: '', code: '', date: null });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deletePurchaseOrder.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <React.Fragment>
            <div className="w-100 p-2 d-flex align-items-center">
                <div className="ms-auto d-flex flex-row gap-1">
                    {
                        setShowFilters &&
                        <FilterBtn
                            showFilters={showFilters ?? false}
                            setShowFilters={setShowFilters}
                        />
                    }
                </div>
            </div>
            <div className={`d-flex flex-row transition-wrapper ${!showFilters && 'gap-0'}`}>
                <div className={`content ${showFilters ? "shrink" : ""}`}>
                    <CustomDataGrid
                        name={language['PurchaseOrders']}
                        newAddComponent="PurchaseOrders"
                        rows={purchaseOrders || []}
                        form={form}
                        page={page}
                        modal={modal}
                        errors={errors}
                        columns={columns}
                        loading={isLoading}
                        pageSize={pageSize}
                        setPage={setPage}
                        setForm={setForm}
                        setModal={setModal}
                        addItems={addItems}
                        setErrors={setErrors}
                        setPageSize={setPageSize}
                        handleDelete={handleDelete}
                        showColumnModal={['description', 'code', 'date']}
                        hideColumns={['purchaseOrderLines', 'id', 'purchaseOrderTypeId']}
                        handleSave={function (): Promise<void> {
                            throw new Error('Function not implemented.');
                        }} />
                </div>

                <div
                    className={`filter-section ${showFilters ? "expand" : ""}`}
                    style={{ border: '1px solid rgba(224, 224, 224, 1)', background: darkMode ? darkModeBackgroud : 'white' }}
                >
                    {
                        showFilters
                        && <FilterPurchaseComponent
                            page={page}
                            pageSize={pageSize}
                            filterData={filterData}
                            showFilters={showFilters}
                            setPage={setPage}
                            setFilterData={setFilterData}
                        />
                    }
                </div>
            </div>
        </React.Fragment>
    );
}
export default PurchaseOrders;